import { useQuery, useQueryClient } from '@tanstack/react-query';

import { showSuccess } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { getScopedTranslate } from '@locale/translate';
import { createCountStore } from '@stores/create-count-store';

import { useOrderedQueryResults } from 'api/helpers';

import {
  addCompanyUserFavorite,
  deleteCompanyUserFavorite,
  loadCompanyUserFavoriteList,
} from './requests';

const favoritesQueryKey = ['favorites'];

export const useInvalidateFavorites = () => {
  const queryClient = useQueryClient();
  const invalidateFavorites = () => queryClient.invalidateQueries(favoritesQueryKey);

  return { invalidateFavorites };
};

const cacheStore = createCountStore();

export const useCachedFavorites = () => {
  cacheStore.useStore();
  const queryClient = useQueryClient();
  const data = queryClient.getQueryData(favoritesQueryKey) as ReturnType<
    typeof useFavorites
  >['data'];

  return data ?? [];
};

const useFavorites = () => {
  return useQuery({
    queryKey: favoritesQueryKey,
    queryFn: async () => {
      const favorites = await loadCompanyUserFavoriteList();
      setTimeout(cacheStore.increase);
      return favorites;
    },
  });
};

export const useFavoritesOrdered = () => {
  const orderedFavorites = useOrderedQueryResults({
    userOptionKey: 'navbarFavoritesOrder',
    useQueryHook: useFavorites,
  });

  return orderedFavorites;
};

export const useAddFavorite = () => {
  const { t } = getScopedTranslate(locale);
  const { invalidateFavorites } = useInvalidateFavorites();

  return useMutation(addCompanyUserFavorite, {
    onSuccess: () => {
      invalidateFavorites();
      showSuccess(t('addToFavorites'));
    },
  });
};

export const useRemoveFavorite = () => {
  const { invalidateFavorites } = useInvalidateFavorites();
  const { t } = getScopedTranslate(locale);

  return useMutation(deleteCompanyUserFavorite, {
    onSuccess: () => {
      invalidateFavorites();
      showSuccess(t('removeFromFavorites'));
    },
  });
};

const locale = {
  en: {
    addToFavorites: 'Added to favorites',
    removeFromFavorites: 'Removed from favorites',
  },
};
